import { useState, useCallback, useRef } from 'react';
import { InteractionManager, Platform } from 'react-native';
import { useDispatch } from 'react-redux';
import DeviceInfo from 'react-native-device-info';
import { StressTestConfig } from './index';
import { NetworkRequestResult, ReduxOperation } from './StressTestResults';
import { getSecureValue, isWeb } from '@helpers';
import { generateRandomBytes } from '@helpers';

// Machine API endpoint
const MACHINES_API_URL = 'https://gateway-dev.smarfac.com/odata/Machine?$orderby=FullName%20asc&$filter=IsActive%20eq%20true';

interface UseStressTestReturn {
  isRunning: boolean;
  networkRequests: NetworkRequestResult[];
  reduxOperations: ReduxOperation[];
  startTest: () => void;
  stopTest: () => void;
  clearResults: () => void;
  exportResults: (metrics: any) => Promise<any>;
}

const useStressTest = (config: StressTestConfig): UseStressTestReturn => {
  const dispatch = useDispatch();
  const [isRunning, setIsRunning] = useState(false);
  const [networkRequests, setNetworkRequests] = useState<NetworkRequestResult[]>([]);
  const [reduxOperations, setReduxOperations] = useState<ReduxOperation[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdCounter = useRef(0);
  const operationIdCounter = useRef(0);

  // Helper to generate unique IDs
  const getNextRequestId = () => {
    requestIdCounter.current += 1;
    return requestIdCounter.current.toString().padStart(3, '0');
  };

  const getNextOperationId = () => {
    operationIdCounter.current += 1;
    return operationIdCounter.current.toString().padStart(3, '0');
  };

  // Get authentication headers for requests
  const getAuthHeaders = async () => {
    const headers: any = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    // Add authentication token
    const token = getSecureValue('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add SignalR connection IDs
    headers['AppHub-Connection-Id'] = getSecureValue('appHubID') || null;
    headers['OnboardingHub-Connection-Id'] = getSecureValue('signalRAnonymID') || null;

    // Add device information
    if (!isWeb) {
      headers['X-Device-ID'] = await DeviceInfo.getUniqueId();
      headers['X-Device-Type'] = 'App';
    } else {
      const getUniqueId = getSecureValue('getUniqueId');
      if (getUniqueId) {
        headers['X-Device-ID'] = getUniqueId;
        headers['X-Device-Type'] = 'App';
      }
    }

    // Add correlation ID for tracing
    headers['X-Correlation-Id'] = `stress-test-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    return headers;
  };

  // Perform a single network request with timing
  const performNetworkRequest = async (requestIndex: number, delayMs: number = 0): Promise<NetworkRequestResult> => {
    const requestId = getNextRequestId();
    const request: NetworkRequestResult = {
      id: requestId,
      url: MACHINES_API_URL,
      method: 'GET',
      startTime: performance.now(),
      status: 'pending',
    };

    // Add pending request to state
    setNetworkRequests((prev) => [...prev, request]);

    // Track which workaround was used
    const workaroundsUsed: string[] = [];

    try {
      // Apply workarounds if configured
      if (config.workarounds.useSetTimeout && config.workarounds.timeoutDelay > 0) {
        await new Promise((resolve) => setTimeout(resolve, config.workarounds.timeoutDelay));
        workaroundsUsed.push(`setTimeout(${config.workarounds.timeoutDelay}ms)`);
      }

      if (config.workarounds.useInteractionManager) {
        await new Promise((resolve) => {
          InteractionManager.runAfterInteractions(() => {
            resolve(undefined);
          });
        });
        workaroundsUsed.push('InteractionManager');
      }

      // Additional delay for batching simulation
      if (delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      // Get auth headers
      const authHeaders = await getAuthHeaders();

      // Perform the actual network request
      const fetchStartTime = performance.now();
      const networkTimingStart = {
        dns: performance.now(),
        connection: 0,
        ttfb: 0,
        download: 0,
      };

      const response = await fetch(MACHINES_API_URL, {
        method: 'GET',
        headers: authHeaders,
        signal: abortControllerRef.current?.signal,
      });

      // Simulate network timing measurements
      const connectionTime = performance.now();
      networkTimingStart.connection = connectionTime - networkTimingStart.dns;

      const responseText = await response.text();
      const downloadEndTime = performance.now();

      // Calculate timings
      const endTime = performance.now();
      const duration = endTime - request.startTime;
      const ttfb = connectionTime - fetchStartTime;
      const downloadTime = downloadEndTime - connectionTime;

      // Update request with results
      const updatedRequest: NetworkRequestResult = {
        ...request,
        endTime,
        duration,
        status: response.ok ? 'success' : 'error',
        statusCode: response.status,
        responseSize: new Blob([responseText]).size,
        networkTiming: config.metrics.trackNetworkTiming
          ? {
              dns: networkTimingStart.dns ? connectionTime - networkTimingStart.dns : undefined,
              connection: networkTimingStart.connection,
              ttfb: ttfb,
              download: downloadTime,
            }
          : undefined,
        workaroundUsed: workaroundsUsed.length > 0 ? workaroundsUsed.join(', ') : undefined,
      };

      // Update state with completed request
      setNetworkRequests((prev) => prev.map((r) => (r.id === requestId ? updatedRequest : r)));

      // Trigger Redux operation
      if (response.ok) {
        const data = JSON.parse(responseText);
        await performReduxOperation(data, requestId);
      }

      return updatedRequest;
    } catch (error: any) {
      const endTime = performance.now();
      const errorRequest: NetworkRequestResult = {
        ...request,
        endTime,
        duration: endTime - request.startTime,
        status: 'error',
        error: error.message || 'Network request failed',
        workaroundUsed: workaroundsUsed.length > 0 ? workaroundsUsed.join(', ') : undefined,
      };

      setNetworkRequests((prev) => prev.map((r) => (r.id === requestId ? errorRequest : r)));

      return errorRequest;
    }
  };

  // Perform Redux storage operation with timing
  const performReduxOperation = async (data: any, requestId: string) => {
    const operationId = getNextOperationId();
    const operation: ReduxOperation = {
      id: operationId,
      action: 'SAVE_MACHINE_ADDRESSES',
      startTime: performance.now(),
      status: 'pending',
    };

    // Add pending operation to state
    setReduxOperations((prev) => [...prev, operation]);

    try {
      const dispatchStartTime = performance.now();
      let stateUpdateCount = 0;

      // Mock Redux dispatch with timing
      // In real implementation, this would dispatch actual Redux actions
      const mockReduxSave = async () => {
        // Simulate Redux dispatch
        await new Promise((resolve) => setTimeout(resolve, 10)); // Simulate dispatch time

        // Count items if data is array
        const itemCount = Array.isArray(data?.value) ? data.value.length : 1;
        const dataSize = new Blob([JSON.stringify(data)]).size;

        // Simulate state updates (would be tracked via middleware in production)
        stateUpdateCount = Math.floor(Math.random() * 3) + 1; // Mock 1-3 state updates

        return { itemCount, dataSize };
      };

      const { itemCount, dataSize } = await mockReduxSave();
      const endTime = performance.now();

      const updatedOperation: ReduxOperation = {
        ...operation,
        endTime,
        duration: endTime - operation.startTime,
        status: 'completed',
        itemCount,
        dataSize,
        stateUpdateCount: config.metrics.trackReduxPerformance ? stateUpdateCount : undefined,
      };

      setReduxOperations((prev) => prev.map((op) => (op.id === operationId ? updatedOperation : op)));
    } catch (error: any) {
      const endTime = performance.now();
      const errorOperation: ReduxOperation = {
        ...operation,
        endTime,
        duration: endTime - operation.startTime,
        status: 'error',
        error: error.message || 'Redux operation failed',
      };

      setReduxOperations((prev) => prev.map((op) => (op.id === operationId ? errorOperation : op)));
    }
  };

  // Execute requests with batching support
  const executeBatch = async (batchRequests: number[], batchIndex: number) => {
    const promises = batchRequests.map((reqIndex) => {
      const delayMs = config.workarounds.useBatching
        ? (reqIndex % config.workarounds.batchSize) * 100 // Stagger within batch
        : 0;
      return performNetworkRequest(reqIndex, delayMs);
    });

    return Promise.all(promises);
  };

  // Start the stress test
  const startTest = useCallback(async () => {
    setIsRunning(true);
    clearResults();

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    const totalRequests = config.concurrentRequests;
    const requestIndices = Array.from({ length: totalRequests }, (_, i) => i);

    try {
      if (config.workarounds.useBatching) {
        // Execute requests in batches
        const batchSize = config.workarounds.batchSize;
        const batches: number[][] = [];

        for (let i = 0; i < requestIndices.length; i += batchSize) {
          batches.push(requestIndices.slice(i, i + batchSize));
        }

        // Execute batches with delay between them
        for (let i = 0; i < batches.length; i++) {
          if (abortControllerRef.current?.signal.aborted) break;

          await executeBatch(batches[i], i);

          // Add delay between batches
          if (i < batches.length - 1 && config.workarounds.useSetTimeout) {
            await new Promise((resolve) => setTimeout(resolve, config.workarounds.timeoutDelay));
          }
        }
      } else {
        // Execute all requests concurrently (no batching)
        await Promise.all(requestIndices.map((index) => performNetworkRequest(index, 0)));
      }
    } catch (error) {
      console.error('Stress test error:', error);
    } finally {
      setIsRunning(false);
    }
  }, [config]);

  // Stop the stress test
  const stopTest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsRunning(false);
  }, []);

  // Clear all results
  const clearResults = useCallback(() => {
    setNetworkRequests([]);
    setReduxOperations([]);
    requestIdCounter.current = 0;
    operationIdCounter.current = 0;
  }, []);

  // Export results to JSON
  const exportResults = useCallback(
    async (metrics: any) => {
      const exportData = {
        testConfig: config,
        metrics: metrics,
        networkRequests: networkRequests,
        reduxOperations: reduxOperations,
        summary: {
          totalRequests: networkRequests.length,
          successfulRequests: networkRequests.filter((r) => r.status === 'success').length,
          failedRequests: networkRequests.filter((r) => r.status === 'error').length,
          avgResponseTime: networkRequests.filter((r) => r.duration).reduce((sum, r) => sum + (r.duration || 0), 0) / networkRequests.length || 0,
          totalReduxOperations: reduxOperations.length,
          avgReduxDuration:
            reduxOperations.filter((op) => op.duration).reduce((sum, op) => sum + (op.duration || 0), 0) / reduxOperations.length || 0,
        },
        deviceInfo: {
          platform: Platform.OS,
          version: Platform.Version,
          isTV: Platform.isTV,
        },
        timestamp: new Date().toISOString(),
      };

      // In a real app, this would save to file or share
      console.log('Export data:', exportData);

      // For web, create downloadable file
      if (Platform.OS === 'web') {
        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
          type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `stress-test-results-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      return exportData;
    },
    [config, networkRequests, reduxOperations]
  );

  return {
    isRunning,
    networkRequests,
    reduxOperations,
    startTest,
    stopTest,
    clearResults,
    exportResults,
  };
};

export default useStressTest;
